import React, { useState } from "react";
import moment from "moment";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { COLOR_WHITE, LINEAR_GRADIENT_BG, PRODUCTION_STATUS_PERMISSIONS, PRODUCTION_STATUS_FLOW, USER_KEY, checkProductionStatusPermission } from "../../const";
import { useSelector } from "react-redux";
import { convertOrderStatus, covertTimeZone, numberFormat } from "../../helper";
import { errorSwal, successSwal } from "../../helper/sweetalert";
import { useMutation, useQuery } from "@apollo/client";
import { GET_ORDER_GROUPS_V2, GET_TOTAL_ORDERGROUP_STATUS, UPDATE_PRODUCTION_STATUS, UPDATE_PRODUCTION_STATUS_MANY } from "../../apollo/ordergroup/query";
import ReactPaginate from "react-paginate";
import { useTranslation } from "react-i18next";
import SalepageOrderDetailNew from "./SalepageOrderDetailNew";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import { MdOutlineDateRange } from "react-icons/md";
import { QRCodeSVG } from "qrcode.react";
import { AiOutlineQrcode, AiOutlineCopy } from "react-icons/ai";

const productionStatusKeys = [
  { value: "PRODUCTION_ORDER", labelKey: "ps_production_order" },
  { value: "PATTERN", labelKey: "ps_pattern" },
  { value: "PRINTING", labelKey: "ps_printing" },
  { value: "HEAT_TRANSFER", labelKey: "ps_heat_transfer" },
  { value: "CUTTING", labelKey: "ps_cutting" },
  { value: "SEWING", labelKey: "ps_sewing" },
  { value: "QC", labelKey: "ps_qc" },
  { value: "READY_TO_COMPLETE", labelKey: "ps_ready_to_complete" },
  { value: "COMPLETED", labelKey: "ps_completed" }
];

function SalepageOrderListNew() {
  const { t } = useTranslation();
  const shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
  const [_limit] = useState(20);
  const [_skip, setSkip] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [cdFillter, setCdFillter] = useState({
    createdAt_gte: "",
    createdAt_lt: "",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [receiveDate, setReceiveDate] = useState("");
  const [selectedOrderGroup, setSelectedOrderGroup] = useState(null);
  const [showDetailModal, setshowDetailModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [updateMode, setUpdateMode] = useState("many");
  const [singleUpdateId, setSingleUpdateId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrOrderCode, setQrOrderCode] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const openQrModal = (code) => {
    const shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
    const base = "https://salepage.packxi.shop";
    const url = shopId ? `${base}/shop/${shopId}/trackOrder/detail/${code}` : `${base}/trackOrder/detail/${code}`;
    setQrUrl(url);
    setQrOrderCode(code);
    setQrModalOpen(true);
    setCopied(false);
  };

  const normalizeRangeForWhere = (range) => {
    const gte = range?.createdAt_gte;
    const lt = range?.createdAt_lt;
    if (!gte || !lt) return null;

    const isDateOnly = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
    const startIso = isDateOnly(gte)
      ? moment(gte).startOf("day").toISOString()
      : moment(gte).toISOString();
    const endIso = isDateOnly(lt)
      ? moment(lt).endOf("day").toISOString()
      : moment(lt).toISOString();
    return { createdAt_gte: startIso, createdAt_lt: endIso };
  };

  const normalizedDateRange = normalizeRangeForWhere(cdFillter);

  const { rolePermissions } = useSelector((state) => state.permissions);
  const userData = JSON.parse(localStorage.getItem(USER_KEY));
  const isAdmin = userData?.data?.role === "ADMIN";
  const canEditReceiveDate = isAdmin || rolePermissions.includes("SALE_ACCESS") || rolePermissions.includes("REPORT_SALE_ONLY") || rolePermissions.includes("CAN_UPDATE_PRODUCTION_ORDER");
  
  // Get statuses that user has permission to handle
  const allowedStatuses = Object.keys(PRODUCTION_STATUS_PERMISSIONS).filter(status => 
    checkProductionStatusPermission(status, rolePermissions)
  );

  // Get statuses that user can view (allowed + next in flow)
  const visibleStatuses = Array.from(new Set([
    ...allowedStatuses,
    ...allowedStatuses.map(status => PRODUCTION_STATUS_FLOW[status]).filter(Boolean)
  ]));

  const baseWhere = {
    shop: shopId,
    isDeleted: false,
    type: ["SALE_PAGE", "KEY_IN"],
    isPaymented: true,
    ...(searchText ? { code: searchText } : {}),
    ...(normalizedDateRange ? normalizedDateRange : {}),
    ...(!isAdmin ? { productionStatus: visibleStatuses } : {}),
  };
  const where = {
    ...baseWhere,
    ...(statusFilter !== "ALL" ? { productionStatus: statusFilter } : {}),
  };

  const { data, loading, refetch } = useQuery(GET_ORDER_GROUPS_V2, {
    variables: {
      where,
      limit: _limit,
      skip: (_skip - 1) * _limit,
      orderBy: "createdAt_DESC",
    },
    fetchPolicy: "cache-and-network",
  });
  const { data: statusTotalData, refetch: refetchStatusTotals } = useQuery(GET_TOTAL_ORDERGROUP_STATUS, {
    variables: {
      where: baseWhere,
    },
    fetchPolicy: "cache-and-network",
  });
  const [updateProductionStatus, { loading: updatingStatus }] = useMutation(UPDATE_PRODUCTION_STATUS);
  const [updateProductionStatusMany, { loading: updatingManyStatus }] = useMutation(UPDATE_PRODUCTION_STATUS_MANY);

  const orderGroupList = data?.orderGroupsV2?.data || [];
  const orderGroupTotal = data?.orderGroupsV2?.total || 0;
  const statusTotals = (statusTotalData?.totalOrdergroupStatusFromSP || []).reduce(
    (acc, item) => {
      const total = item?.total || 0;
      const key = item?.productionStatus || "UNKNOWN";
      acc[key] = total;
      acc.ALL += total;
      return acc;
    },
    { ALL: 0 }
  );
  const productionStatuses = productionStatusKeys.map((s) => ({ value: s.value, label: t(s.labelKey) }));
  const updatableStatuses = productionStatuses.filter(status => 
    checkProductionStatusPermission(status.value, rolePermissions)
  );
  const nextStatusesBulk = Array.from(new Set(
    selectedIds
      .map(id => {
        const o = orderGroupList.find(x => x.id === id);
        return PRODUCTION_STATUS_FLOW[o?.productionStatus];
      })
      .filter(Boolean)
  ));
  const nextStatusesBulkLabel = nextStatusesBulk
    .map(ns => productionStatuses.find(s => s.value === ns)?.label)
    .filter(Boolean)
    .join(", ");
  const statusTabConfigs = [
    { key: "ALL", label: t("ps_all"), color: "#0EA5E9", bg: "#E0F2FE" },
    { key: "PRODUCTION_ORDER", label: t("ps_production_order"), color: "#F59E0B", bg: "#FFFBEB" },
    { key: "PATTERN", label: t("ps_pattern"), color: "#F43F5E", bg: "#FFE4E6" },
    { key: "PRINTING", label: t("ps_printing"), color: "#F97316", bg: "#FFEDD5" },
    { key: "HEAT_TRANSFER", label: t("ps_heat_transfer"), color: "#EAB308", bg: "#FEF9C3" },
    { key: "CUTTING", label: t("ps_cutting"), color: "#10B981", bg: "#D1FAE5" },
    { key: "SEWING", label: t("ps_sewing"), color: "#3B82F6", bg: "#DBEAFE" },
    { key: "QC", label: t("ps_qc"), color: "#8B5CF6", bg: "#EDE9FE" },
    { key: "READY_TO_COMPLETE", label: t("ps_ready_to_complete"), color: "#D946EF", bg: "#FDF4FF" },
    { key: "COMPLETED", label: t("ps_completed"), color: "#16A34A", bg: "#DCFCE7" },
  ];
  const getStatusTabConfig = (status) => statusTabConfigs.find((tab) => tab.key === status);
  const isOrderAllowed = (order) => allowedStatuses.includes(order?.productionStatus);

  const onSeletedAllOrder = () => {
    if (isSelectAll) {
      setSelectedIds([]);
      setIsSelectAll(false);
    } else {
      const selectableIds = orderGroupList
        .filter(o => o.productionStatus !== "COMPLETED" && (isAdmin || isOrderAllowed(o) || canEditReceiveDate))
        .map((o) => o.id);
      setSelectedIds(selectableIds);
      setIsSelectAll(true);
    }
  };

  const getSeletedOrder = (id) => {
    if (selectedIds.includes(id)) {
      const nextIds = selectedIds.filter((i) => i !== id);
      setSelectedIds(nextIds);
      setIsSelectAll(false);
    } else {
      const nextIds = [...selectedIds, id];
      setSelectedIds(nextIds);
      if (nextIds.length === orderGroupList.length && orderGroupList.length > 0) {
        setIsSelectAll(true);
      }
    }
  };
  const handleBulkUpdateStatus = async () => {
    if (!selectedIds.length) return;
    try {
      const parsed = canEditReceiveDate && receiveDate ? moment(receiveDate, "DD/MM/YYYY", true) : null;
      if (canEditReceiveDate && receiveDate && (!parsed || !parsed.isValid())) throw new Error("Invalid receive date");
      const utcDateTime = parsed ? parsed.startOf("day").toISOString() : null;

      // Admin + manual pick -> single bulk write with chosen status
      if (isAdmin && selectedStatus) {
        await updateProductionStatusMany({
          variables: {
            where: { id_in: selectedIds },
            data: {
              status: selectedStatus,
              ...(utcDateTime ? { receiveDate: utcDateTime } : {}),
            },
          },
        });
      } else {
        // Group by next status and bulk write per group
        const groups = selectedIds.reduce((acc, id) => {
          const order = orderGroupList.find(o => o.id === id);
          const nextStatus = PRODUCTION_STATUS_FLOW[order?.productionStatus] || order?.productionStatus;
          if (!nextStatus) return acc;
          if (!acc[nextStatus]) acc[nextStatus] = [];
          acc[nextStatus].push(id);
          return acc;
        }, {});

        for (const [statusKey, ids] of Object.entries(groups)) {
          await updateProductionStatusMany({
            variables: {
              where: { id_in: ids },
              data: {
                status: statusKey,
                ...(utcDateTime ? { receiveDate: utcDateTime } : {}),
              },
            },
          });
        }
      }
      
      successSwal(t("update_success"));
      setSelectedIds([]);
      setIsSelectAll(false);
      refetch();
      refetchStatusTotals();
    } catch (error) {
      errorSwal(error.message);
    }
  };
  const handleSingleUpdateStatus = async () => {
    if (!singleUpdateId) return;
    try {
      const parsed = canEditReceiveDate && receiveDate ? moment(receiveDate, "DD/MM/YYYY", true) : null;
      if (canEditReceiveDate && receiveDate && (!parsed || !parsed.isValid())) throw new Error("Invalid receive date");
      const utcDateTime = parsed ? parsed.startOf("day").toISOString() : null;

      let nextStatus = selectedStatus;
      if (!isAdmin || !selectedStatus) {
        const order = orderGroupList.find(o => o.id === singleUpdateId);
        nextStatus = PRODUCTION_STATUS_FLOW[order?.productionStatus] || order?.productionStatus;
      }

      await updateProductionStatus({
        variables: {
          where: { id: singleUpdateId },
          data: {
            status: nextStatus,
            ...(utcDateTime ? { receiveDate: utcDateTime } : {}),
          },
        },
      });
      successSwal(t("update_success"));
      refetch();
      refetchStatusTotals();
    } catch (error) {
      errorSwal(error.message);
    }
  };

  const handleCopyLinkLive = async (orderId) => {
    try {
      const shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
      const base = "https://salepage.packxi.shop";
      const url = shopId ? `${base}/shop/${shopId}/order-confirm/${orderId}` : `${base}/order-confirm/${orderId}`;
      await navigator.clipboard.writeText(url);
      successSwal(t("copy_link_success"));
    } catch (error) {
      console.error("Copy failed: ", error);
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      successSwal("ຄັດລອກສຳເລັດ");
    } catch (error) {
      console.error("Copy failed: ", error);
    }
  };

  const handleCopyQrUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy failed: ", error);
    }
  };

  const handleDownloadQr = () => {
    const svg = document.getElementById("qr-code-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `QR_${qrOrderCode}.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const getStatusBadgeStyle = (status) => {
    const config = getStatusTabConfig(status);
    if (!config) return {};
    return {
      background: "transparent",
      color: config.color,
      border: `1px solid ${config.color}`,
    };
  };

  return (
    <>
      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <Row className="g-2">
            {statusTabConfigs.map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <Col key={tab.key} xs={6} md={3} lg={2}>
                  <div
                    role="button"
                    onClick={() => {
                      setStatusFilter(tab.key);
                      setSkip(1);
                      setSelectedIds([]);
                      setIsSelectAll(false);
                    }}
                    style={{
                      border: `1px solid ${tab.color}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      background: isActive ? tab.color : COLOR_WHITE,
                      color: isActive ? COLOR_WHITE : tab.color,
                      minHeight: 54,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{tab.label}</div>
                    <div style={{ fontSize: 11, marginTop: 2, opacity: isActive ? 0.9 : 0.8 }}>
                      {t("ps_count")}: {statusTotals[tab.key] || 0}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Label className="mb-1">{t("order_code")}</Form.Label>
              <Form.Control
                placeholder={t("search_order_or_phone")}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setSkip(1);
                }}
              />
            </Col>
            <Col md={6} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Form.Label className="mb-1">{t("day_month_year")}</Form.Label>
              <Button
                variant="outline-secondary"
                onClick={() => setIsCurrentModals(true)}
                style={{ textAlign: "left", width: "100%" }}
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <MdOutlineDateRange style={{ fontSize: 24 }} />
                )}
                &nbsp;&nbsp;
                {!(cdFillter?.createdAt_gte && cdFillter?.createdAt_lt) ? (
                  "mm/dd/yyyy"
                ) : (
                  <>
                    {covertTimeZone(cdFillter?.createdAt_gte) +
                      " - " +
                      covertTimeZone(cdFillter?.createdAt_lt)}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <CurrentDateComponents
        openModal={isCurrentModals}
        onCloseModal={() => setIsCurrentModals(false)}
        setCdFillter={(next) => {
          setCdFillter(next);
          setSkip(1);
          setSelectedIds([]);
          setIsSelectAll(false);
        }}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
      />

      <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="order_index"
                label={t("order_index")}
                checked={isSelectAll}
                onChange={onSeletedAllOrder}
              />
            </Col>
            <Col md={6} className="text-end">
              <span>{t("total_amount")}: {orderGroupTotal}</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
          <Card.Body>
            <div className="text-center" style={{ padding: "16px 0", color: "#6B7280" }}>
              <Spinner animation="border" size="sm" className="me-2" />
              {t("loading_text")}
            </div>
          </Card.Body>
        </Card>
      ) : orderGroupList.length === 0 ? (
        <Card className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
          <Card.Body>
            <div className="text-center" style={{ padding: "16px 0", color: "#6B7280" }}>
              {t("no_data_found")}
            </div>
          </Card.Body>
        </Card>
      ) : (
        orderGroupList.map((order) => {
          const orderType = order?.type || order?.orders?.[0]?.type;
          const isSelected = selectedIds.includes(order?.id);
          const orderItems = (order?.orders || []).filter((o) => !o?.isDeleted);
          const totalQty = orderItems.reduce((sum, o) => sum + Number(o?.amount || 0), 0);
          const shopSizeText = orderItems
            .map((o) => (o?.shopSize || "").trim())
            .filter(Boolean)
            .join(" | ");
          return (
            <Card key={order?.id} className="mb-3" style={{ border: "none", background: COLOR_WHITE }}>
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={1}>
                    <Form.Check
                      checked={isSelected}
                      disabled={order?.productionStatus === "COMPLETED" || (!isAdmin && !allowedStatuses.includes(order?.productionStatus) && !canEditReceiveDate)}
                      onChange={() => getSeletedOrder(order?.id)}
                      type="checkbox"
                    />
                  </Col>
                  <Col md={3}>
                    <div className="fw-bold text-primary d-flex align-items-center gap-2">
                      {order?.code ?? "-"}
                      {order?.code && (
                        <AiOutlineCopy
                          style={{ cursor: "pointer", color: "#6B7280" }}
                          onClick={() => handleCopyCode(order.code)}
                        />
                      )}
                    </div>
                    <div style={{ fontSize: 12 }}>{t("delivery")}: {order?.logistic}</div>
                    <div style={{ fontSize: 12 }}>{t("delivery_address_label")}: {order?.destinationLogistic}</div>
                  </Col>
                  <Col md={3}>
                    <div>{order?.cfName ?? order?.customerName ?? "-"}</div>
                    <div style={{ fontSize: 12 }}>{order?.phone ?? "-"}</div>
                  </Col>
                  <Col md={2}>
                    <span style={{ ...getStatusBadgeStyle(order?.productionStatus), display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 12 }}>
                      {productionStatuses.find((status) => status.value === order?.productionStatus)?.label || convertOrderStatus(order?.productionStatus)}
                    </span>
                    <div style={{ fontSize: 12, marginTop: 6 }}>{orderType || "-"}</div>
                  </Col>
                  <Col md={3} className="text-end">
                                      <div style={{ fontSize: 12 }}>{t("ps_count")}: {numberFormat(totalQty)}</div>
                    <div style={{ fontSize: 12 }}>ໄຊ້: {shopSizeText || "-"}</div>
                    <div>₭ {numberFormat(order?.totalPrice ?? 0)}</div>
                    <div style={{ fontSize: 12 }}>฿ {numberFormat(order?.sumPriceBaht ?? 0)}</div>
                    <div style={{ fontSize: 12 }}>$ {numberFormat(order?.sumPriceUsd ?? 0)}</div>
                  </Col>
                </Row>

                <Row className="mt-3 align-items-center">
                  <Col md={6}>
                    <div style={{ fontSize: 12 }}>{t("day_month_year")}: {moment(order?.createdAt).format("DD/MM/YYYY HH:mm")}</div>
                    <div style={{ fontSize: 12,color:"red" }}>{t("receive_date")}: {order?.receiveDate ? moment(order?.receiveDate).format("DD/MM/YYYY") : "-"}</div>
                  </Col>
                  <Col md={6} className="text-end">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-2"
                      style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                      onClick={() => openQrModal(order?.code)}
                    >
                      <AiOutlineQrcode />
                      {t("show_qr") || "ສະແດງ QR"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-2"
                      onClick={() => {
                        setSelectedOrderGroup(order);
                        setshowDetailModal(true);
                      }}
                    >
                      {t("order_detail_title")}
                    </Button>
                    <Button
                      size="sm"
                      style={{ background: LINEAR_GRADIENT_BG, color: COLOR_WHITE, border: "none" }}
                      className="me-2"
                      disabled={order?.productionStatus === "COMPLETED" || (!isAdmin && !allowedStatuses.includes(order?.productionStatus) && !canEditReceiveDate)}
                      onClick={() => {
                        setSingleUpdateId(order?.id);
                        setUpdateMode("single");
                        setSelectedStatus(isAdmin ? (PRODUCTION_STATUS_FLOW[order?.productionStatus] || order?.productionStatus || "") : (order?.productionStatus ?? ""));
                        setReceiveDate(order?.receiveDate ? moment(order?.receiveDate).format("DD/MM/YYYY") : "");
                        setShowBulkStatusModal(true);
                      }}
                    >
                      {t("update_status")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleCopyLinkLive(order?.id)}
                    >
                      {t("copy_link")}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          );
        })
      )}

      {orderGroupTotal > 0 && (
        <div className="d-flex justify-content-center mt-3 mb-3">
          <ReactPaginate
            previousLabel={t("previous_pagination_title")}
            onPageChange={(event) => {
              setIsSelectAll(false);
              setSelectedIds([]);
              setSkip(event.selected + 1);
            }}
            breakLabel="..."
            pageRangeDisplayed={2}
            pageCount={Math.ceil(orderGroupTotal / _limit)}
            marginPagesDisplayed={2}
            renderOnZeroPageCount={null}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={_skip - 1}
            nextLabel={t("next_pagination_title")}
          />
        </div>
      )}
      {selectedIds.length > 0 && (
        <div style={{ position: "fixed", right: 20, bottom: 20, zIndex: 1000 }}>
          <Button
            size="sm"
            style={{ background: "#F59E0B", color: COLOR_WHITE, border: "none",padding: "6px 12px" }}
            disabled={
              updatingStatus ||
              updatingManyStatus ||
              (!selectedIds.every(id => {
                const o = orderGroupList.find(x => x.id === id);
                return o?.productionStatus !== "COMPLETED" && (isAdmin || allowedStatuses.includes(o?.productionStatus) || canEditReceiveDate);
              }))
            }
            onClick={() => {
              setUpdateMode("many");
              setReceiveDate("");
              setShowBulkStatusModal(true);
            }}
          >
            {t("update_all_status")}
          </Button>
        </div>
      )}

      <Modal show={qrModalOpen} onHide={() => setQrModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18, fontWeight: "bold" }}>QR ຕິດຕາມອໍເດີ້</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center", marginBottom: 10, color: "#6b7280", fontSize: 14 }}>
            ສະແກນເພື່ອເຂົ້າໜ້າ “ຕິດຕາມລາຍລະອຽດ”
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <QRCodeSVG
              id="qr-code-svg"
              value={qrUrl || ""}
              size={220}
              bgColor="#ffffff"
              fgColor="#0e2a47"
              level="M"
              includeMargin={false}
            />
          </div>
          <div style={{ background: "#f9fafb", padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: "#111827", marginBottom: 8 }}>
              <span style={{ color: "#9ca3af", marginRight: 8 }}>ອໍເດີ້:</span>
              <span style={{ fontWeight: 700 }}>#{qrOrderCode || "-"}</span>
            </div>
            <div style={{ fontSize: 13, color: "#111827", wordBreak: "break-all" }}>
              <span style={{ color: "#9ca3af", marginRight: 8 }}>ລິ້ງ:</span>
              <span>{qrUrl || "-"}</span>
            </div>
          </div>
          <Row className="g-2">
            <Col xs={6}>
              <Button
                variant={copied ? "success" : "outline-primary"}
                style={{ width: "100%" }}
                onClick={handleCopyQrUrl}
              >
                {copied ? "ຄັດລອກແລ້ວ" : "ຄັດລອກລິ້ງ"}
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                variant="primary"
                style={{ width: "100%" }}
                onClick={handleDownloadQr}
              >
                ບັນທຶກ QR
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        centered
        scrollable
        show={showDetailModal}
        onHide={() => setshowDetailModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("order_detail_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SalepageOrderDetailNew selectedOrderGroup={selectedOrderGroup} />
        </Modal.Body>
      </Modal>
      <Modal
        centered
        show={showBulkStatusModal}
        onHide={() => setShowBulkStatusModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("update_status_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isAdmin ? (
            <>
              <Form.Label>{t("ເລືອກຕຳແໜ່ງ")}</Form.Label>
              <Form.Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                disabled={updatingStatus || updatingManyStatus}
              >
                <option value="">--{t("ເລືອກຕຳແໜ່ງ")}--</option>
                {productionStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Form.Select>
            </>
          ) : (
            <div className="mb-3">
              <Form.Label>
                {updateMode === "single"
                  ? `${t("ສະຖານະຕໍ່ໄປຫຼັງຈາກອັບເດດ")}: ${productionStatuses.find(s => s.value === PRODUCTION_STATUS_FLOW[orderGroupList.find(o => o.id === singleUpdateId)?.productionStatus])?.label || "-"}`
                  : `${t("ສະຖານະຕໍ່ໄປຫຼັງຈາກອັບເດດ")}: ${nextStatusesBulkLabel || ""}`
                }
              </Form.Label>
              <div className="p-2 border rounded bg-light" style={{ fontWeight: "bold" }}>
                {updateMode === "single" ? (
                  productionStatuses.find(s => s.value === PRODUCTION_STATUS_FLOW[orderGroupList.find(o => o.id === singleUpdateId)?.productionStatus])?.label || "-"
                ) : (
                  `${t("ອັບເດດທຸກອໍເດີທີ່ເລືອກເປັນສະຖານະ")} ${nextStatusesBulkLabel || ""}`
                )}
              </div>
            </div>
          )}
          <Form.Label className="mt-3">{t("receive_date")}</Form.Label>
          <Form.Control
            type="date"
            disabled={updatingStatus || updatingManyStatus || !canEditReceiveDate}
            value={
              receiveDate
                ? moment(receiveDate, "DD/MM/YYYY", true).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) => {
              const iso = e?.target?.value;
              setReceiveDate(iso ? moment(iso).format("DD/MM/YYYY") : "");
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ background: "transparent", color: LINEAR_GRADIENT_BG, border: `1px solid ${LINEAR_GRADIENT_BG}` }}
            onClick={() => setShowBulkStatusModal(false)}
            disabled={updatingStatus}
          >
            {t("close")}
          </Button>
          <Button
            style={{ background: LINEAR_GRADIENT_BG, color: COLOR_WHITE, border: "none" }}
            disabled={updatingStatus || updatingManyStatus || (isAdmin && !selectedStatus)}
            onClick={async () => {
              if (updateMode === "single") {
                await handleSingleUpdateStatus();
              } else {
                await handleBulkUpdateStatus();
              }
              setShowBulkStatusModal(false);
            }}
          >
            {t("save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SalepageOrderListNew;
